import { useEffect, useState } from 'react';
import { Button } from './ui.js';
import './consent.css';

/**
 * Cookie consent + analytics loader (mirrors the marketing site's consent.js).
 * The choice is stored in a `za_consent` cookie on .zestassure.com, so a
 * visitor who already answered on www.zestassure.com is never asked again
 * here (and vice versa). Analytics load only after "all" consent.
 */

const GA_ID = (import.meta.env.VITE_GA_ID as string | undefined) ?? 'G-P18G485Q4C';
const CLARITY_ID = (import.meta.env.VITE_CLARITY_ID as string | undefined) ?? 'xiax77g4cp';
const PINGDOM_ID = (import.meta.env.VITE_PINGDOM_ID as string | undefined) ?? '';

type Consent = 'all' | 'essential' | null;

function readConsent(): Consent {
  const match = document.cookie.match(/(?:^|;\s*)za_consent=(all|essential)/);
  return (match?.[1] as Consent) ?? null;
}

function writeConsent(value: Exclude<Consent, null>): void {
  const domain = /\.zestassure\.com$/.test(location.hostname) ? '; Domain=.zestassure.com' : '';
  document.cookie =
    `za_consent=${value}; Max-Age=${180 * 24 * 3600}; Path=/; SameSite=Lax${domain}` +
    (location.protocol === 'https:' ? '; Secure' : '');
}

let analyticsLoaded = false;

function loadAnalytics(): void {
  if (analyticsLoaded || import.meta.env.MODE === 'test') return;
  analyticsLoaded = true;

  if (GA_ID) {
    const ga = document.createElement('script');
    ga.async = true;
    ga.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(ga);
    const w = window as typeof window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
    w.dataLayer = w.dataLayer ?? [];
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer!.push(args);
    };
    w.gtag('js', new Date());
    w.gtag('config', GA_ID);
  }

  if (CLARITY_ID) {
    const w = window as typeof window & { clarity?: { q?: unknown[] } & ((...args: unknown[]) => void) };
    w.clarity =
      w.clarity ??
      Object.assign(
        (...args: unknown[]) => {
          (w.clarity!.q = w.clarity!.q ?? []).push(args);
        },
        { q: [] as unknown[] },
      );
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
    document.head.appendChild(script);
  }

  if (PINGDOM_ID) {
    (window as typeof window & { _prum?: unknown[] })._prum = [
      ['id', PINGDOM_ID],
      ['mark', 'firstbyte', Date.now()],
    ];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://rum-static.pingdom.net/prum.min.js';
    document.head.appendChild(script);
  }
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = readConsent();
    if (consent === 'all') loadAnalytics();
    else if (consent === null) setVisible(true);
  }, []);

  if (!visible) return null;

  const choose = (value: 'all' | 'essential') => {
    writeConsent(value);
    setVisible(false);
    if (value === 'all') loadAnalytics();
  };

  return (
    <div className="consent-banner" role="region" aria-label="Cookie consent">
      <p>
        We use cookies for analytics to improve Zest Assure — see our{' '}
        <a href="https://www.zestassure.com/policies/cookies/" target="_blank" rel="noreferrer">
          Cookies Policy
        </a>
        . Essential cookies are always on.
      </p>
      <div className="consent-actions">
        <Button size="sm" onClick={() => choose('all')}>
          Accept analytics
        </Button>
        <Button size="sm" variant="secondary" onClick={() => choose('essential')}>
          Essential only
        </Button>
      </div>
    </div>
  );
}
